import React, {
  useReducer,
  useState,
  useContext,
  useCallback,
  useEffect,
  useRef
} from 'react'
import moment from 'moment'
import { Button } from 'syngenta-react-components'
import DetailView from '../../dpe-usco/components/Details/DetailView'
import NewContractDetailsWidget from './NewContractDetailsWidget'
import FieldWithTable from './FieldWithTable'
import EstimatedUnits from './EstimatedUnits'
import DeliveryScheduleWidget from './DeliveryScheduleWidget'
import {
  getContractCommittedUnits,
  createNewContractFieldSync,
  getFieldsBySFDC_IDAndContractId,
  getProductionPlan,
  postStorageLocation,
  getStorage,
  getContractById,
  getTraining
} from '../../services/apiHelper'
import useQuery from '../../services/useQuery'
import { GlobalContext, AuthContext } from '../../services/context'
import TableStatus from '../../components/subComponents/TableStatus/TableStatus'
import Storage from './Storage'
import AccessDeniedView from '../Interstitial/AccessDeniedView'
import { useParams } from 'react-router-dom'

const emptyArray = []

const possibleErrors = {
  grain_receiver_details: {
    empty: {
      state: false,
      value: 'Missing grain receiver info'
    },
    no_contract_template: {
      state: false,
      value: 'Grain receiver is missing a contract template'
    },
    no_production_plan: {
      state: false,
      value: 'Grain receiver has no production plans'
    }
  },
  contract_type: {
    empty: {
      state: false,
      value: 'Please select a contract type'
    }
  },
  contract_year: {
    empty: {
      state: false,
      value: 'Please select a contract year'
    }
  },
  grower_details: {
    empty: {
      state: false,
      value: 'Missing grower info'
    },
    no_fields: {
      state: false,
      value: 'Selected grower does not have any recorded fields'
    }
  },
  reseller_details: {
    empty: {
      state: false,
      value: 'Missing reseller info'
    }
  },
  fields: {
    empty: {
      state: false,
      value: 'Please select and fill out at least one field'
    }
  },
  acres_field_empty: {
    empty: {
      state: false,
      value: 'Please fill out acres field'
    }
  },
  aph_range_field_empty: {
    empty: {
      state: false,
      value: 'APH value must be numeric without decimals between 50 and 300'
    }
  },
  aph_field_empty: {
    empty: {
      state: false,
      value: 'Please fill out Average yield (APH) field'
    }
  },
  planting_field_empty: {
    empty: {
      state: false,
      value: 'Please fill out Plating rate field'
    }
  },
  
  delivery_schedule: {
    empty: {
      state: false,
      value:
        'Please completely fill out at least one month inside the delivery schedule'
    },
    cattle_empty: {
      state: false,
      value: 'Please provide a cattle type and count'
    },
    contract_unit_invalid: {
      state: false,
      value: 'No bushels should be allowed for Oct, Nov, Jan, Mar ,May, July, Aug, Sept as production isn’t set up for these months.'
    }
  },
  storage: {
    empty: {
      state: false,
      value: 'Please select or add at least one storage location'
    }
  },
  generic: {
    api_fail: {
      state: false,
      value:
        'Whoops! Something is not working quite right. Please contact your admin'
    }
  },
  delivery_remaining_bushle_zero: {
    empty: {
      state: false,
      value: 'Remaining bushels must be 0'
    }
  },
  field_aph_planting_rate: {
    empty: {
      state: false,
      value: 'Planting Rate and APH is required'
    }
  },
  planting_rate_invalid: {
    empty: {
      state: false,
      value: 'Planting rate value must be numeric without decimals between 100k - 180k'
    }
  }
}

const NewContractReducer = (state, action) => {
  switch (action.type) {
    case 'UPDATE_ROOT_CONTRACT_TEMPLATE_ID': {
      return {
        ...state,
        root: { ...state.root, contract_template_id: action.payload }
      }
    }
    case 'UPDATE_ROOT_CONTRACT_TYPE': {
      return {
        ...state,
        root: { ...state.root, contract_type: action.payload }
      }
    }
    case 'REMOVE_ROOT_CONTRACT_YEAR': {
      const newRoot = { ...state.root }
      delete newRoot['contract_year']
      return { ...state, root: newRoot }
    }
    case 'UPDATE_ROOT_CONTRACT_YEAR': {
      const contract_year =
        action.payload.contractYear || new Date().getFullYear()
      const production_plan_id = action.payload.production_plan_id
      return {
        ...state,
        root: { ...state.root, contract_year, production_plan_id },
        rightHandStatus: 'ERROR',
        errors: { ...state.errors, productionPlan: null }
      }
    }
    case 'UPDATE_ROOT_CONTRACT_YEAR_START_OR_END': {
      const contract_year = new Date(
        action.payload.contract_start
      ).getFullYear()
      return {
        ...state,
        root: {
          ...state.root,
          contract_year,
          contract_start: action.payload.contract_start,
          contract_end: action.payload.contract_end
        }
      }
    }
    case 'REMOVE_ROOT_CONTRACT_YEAR_START_AND_END': {
      const newRoot = { ...state.root }
      delete newRoot['contract_start']
      delete newRoot['contract_end']
      return { ...state, root: newRoot }
    }
    case 'UPDATE_RESELLER_DETAILS': {
      return {
        ...state,
        reseller_details: action.payload,
        root: {
          ...state.root,
          external_reference_id: action.payload.company_id
        }
      }
      // return { ...state, reseller_details: action.payload }
    }
    case 'REMOVE_RESELLER_DETAILS': {
      return {
        ...state,
        reseller_details: null,
        root: { ...state.root, external_reference_id: null }
      }
      // return { ...state, reseller_details: null }
    }
    case 'UPDATE_GROWER_DETAILS': {
      return { ...state, grower_details: action.payload }
    }
    case 'REMOVE_GROWER_DETAILS': {
      return { ...state, grower_details: null }
    }
    case 'UPDATE_CONTRACT_DETAILS': {
      let crop_year = new Date().getFullYear()
      if (action.payload && action.payload.contractYear) {
        crop_year = action.payload.contractYear
      } else if (action.payload && action.payload.contractStart) {
        crop_year = new Date(action.payload.contractStart).getFullYear()
      }
      return { ...state, contractDetails: action.payload, crop_year }
    }
    case 'UPDATE_GRAIN_RECEIVER_DETAILS':
      return { ...state, grain_receiver_details: action.payload }
    case 'REMOVE_GRAIN_RECEIVER_DETAILS':
      return { ...state, grain_receiver_details: null }
    case 'UPDATE_FIELDS': {
      return { ...state, fields: action.payload }
    }
    case 'UPDATE_ACRES': {
      return { ...state, acres_field_empty: action.payload }
    }
    case 'UPDATE_APH_RANGE': {
      return { ...state, aph_range_field_empty: action.payload }
    }
    case 'UPDATE_APH': {
      return { ...state, aph_field_empty: action.payload }
    }
    case 'UPDATE_PLANTING': {
      return { ...state, planting_field_empty: action.payload }
    }
    case 'UPDATE_DELIVERY_SCHEDULE': {
      return { ...state, delivery_schedule: action.payload }
    }
    case 'UPDATE_STORAGE': {
      return { ...state, storage: action.payload }
    }
    case 'UPDATE_CATTLE_INFO': {
      if (state.cattle_info && state.cattle_info[0]) {
        return {
          ...state,
          cattle_info: [{ ...state.cattle_info[0], ...action.payload }]
        }
      } else {
        return { ...state, cattle_info: [{ ...action.payload }] }
      }
    }
    case 'INIT_AMMEND_CONTRACT': {
      const { contract_year, display_details, contract_language } = action.payload
      return {
        ...state,
        root: {
          ...state.root,
          contract_year,
          contract_language,
          production_plan_id: display_details.production_plan_id
        }
      }
    }
    case 'ATTEMPT_POST_CONTRACT':
      return { ...state, rightHandStatus: 'CREATING' }
    case 'SET_ERRORS': {
      let atLeastOne = false
      const currentErrors = state.errors
      for (let field in currentErrors) {
        const errorField = currentErrors[field]
        for (let error in errorField) {
          if(action.payload[field] && action.payload[field][error] && action.payload[field][error].value){
            currentErrors[field][error].state = true
            currentErrors[field][error].value = action.payload[field][error].value
            atLeastOne = true
          }
          else if (action.payload[field] && action.payload[field][error]) {
            currentErrors[field][error].state = true
            atLeastOne = true
          } else {
            currentErrors[field][error].state = false
          }
        }
      }
      const returnable = { ...state, errors: currentErrors }
      if (atLeastOne) {
        returnable.rightHandStatus = 'ERROR'
      }
      return returnable
    }
    case 'CLEAR_ERROR': {
      const currentErrors = state.errors
      for (let field in currentErrors) {
        const errorField = currentErrors[field]
        for (let error in errorField) {
          if (action.payload[field] && !action.payload[field][error]) {
            currentErrors[field][error].state = false
          }
        }
      }
      return { ...state, errors: currentErrors }
    }
    case 'UPDATE_CONTRACT_ID':{
      return {
        ...state,
        root: { ...state.root, parent_voided_contract_id: action.payload.newContractId, userInfo: action.payload.userInfo }
      }}
    case 'UPDATE_REMAINING_BUSHLES': {
      return { ...state, delivery_remaining_bushle_zero: action.payload }
    }
    case 'UPDATE_FIELD_APH_PLANTING_RATE': {
      return { ...state, field_aph_planting_rate: action.payload }
    }
    default:
      throw new Error()
  }
}
const CreateContractView = () => {
  const { dispatch, trackEvent } = useContext(GlobalContext)
  const { hasAccess } = useContext(AuthContext)
  const { contract_id } = useParams()
  const [contractState, contractDispatch] = useReducer(NewContractReducer, {
    root: {},
    fields: [],
    aph_field_empty: true,
    planting_field_empty: true,
    delivery_remaining_bushle_zero: true,
    field_aph_planting_rate: false,
    errors: possibleErrors
  })
  const [fieldData, setFieldData] = useState({})
  const [, setFieldFormData] = useState({})
  const [fieldDataStatus, setFieldDataStatus] = useState('WAITING')
  const [reseller_id, set_reseller_id] = useState(useQuery().get('reseller_id'))
  const [grower_id, set_grower_id] = useState()
  const [grain_receiver_id, set_grain_receiver_id] = useState(
    useQuery().get('grain_receiver_id')
  )
  const [threePO_id] = useState(useQuery().get('3PO_id'))
  const [deliveryData, setDeliveryData] = useState([])
  const [storageData, setStorageData] = useState([])
  const [hasPermissison, setHasPermissions] = useState('LOADING')
  const [newMonthlyUnits, setNewMonthlyUnits] = useState(false)
  const [
    init_grain_receiver_details,
    set_init_grain_receiver_details
  ] = useState()
  const [
    init_grower_details,
    set_init_grower_details
  ] = useState()
  const [
    init_reseller_details,
    set_init_reseller_details
  ] = useState()


  const [init_contract_details, set_init_contract_details] = useState()
  const [init_fields, set_init_fields] = useState()
  const [init_storage, set_init_storage] = useState()
  const [init_delivery_schedule, set_init_delivery_schedule] = useState()
  const [init_cattle_info, set_init_cattle_info] = useState()
  const [validDataError, setValidDataError] = useState(false)
  const [inventoryCode, setInventoryCode] = useState('')
  const [totalAPH, setTotalAPH] = useState(0)
  const [defaultAphEvent, setDefaultAphEvent] = useState(false)
  const [resetDeliveryTable, setResetDeliveryTable] = useState(false)
  const [satusResetDeliveryTable, setSatusResetDeliveryTable] = useState(false)
  const [sfdcId, setSfdcId] = useState(null)
  const [existedMonthInProdPlan, setExistMonthInProdPlan] = useState([])

  const canceled = useRef(false)

  useEffect(() => {
    setSatusResetDeliveryTable(true)
  },[resetDeliveryTable, setSatusResetDeliveryTable])

  useEffect(() => {
    return () => {
      canceled.current = true
    }
  }, [])

  const fieldDataCallback = useCallback(sfdc_id => {
    setFieldDataStatus('LOADING')
    setSfdcId(sfdc_id)
    getFieldsBySFDC_IDAndContractId(sfdc_id, contract_id).then(response => {
      if (!canceled.current) {
        if (response && response.farmArray && response.farmArray.length) {
          setFieldData(response)
          setFieldDataStatus()
          contractDispatch({
            type: 'CLEAR_ERROR',
            payload: { grower_details: { no_fields: false } }
          })
        } else {
          setFieldData({})
          setFieldDataStatus('EMPTY')
        }
      }
    })
  }, [contract_id])

  const checkForErrors = () => {
    const errors = {}
    if (
      !contractState.grain_receiver_details ||
      !contractState.grain_receiver_details.company_id
    ) {
      errors.grain_receiver_details = { empty: true }
    }
    if (!contractState.root || !contractState.root.contract_type) {
      errors.contract_type = { empty: true }
    }
    if (!contractState.root || !contractState.root.contract_year) {
      errors.contract_year = { empty: true }
    }
    if (!contractState.grower_details) {
      errors.grower_details = { empty: true }
    }
    // if (contractState.fields.length === 0) {
    //   errors.fields = { empty: true }
    // }
    if (contractState.acres_field_empty) {
      errors.acres_field_empty = { empty: true }
    }
    if (contractState.aph_range_field_empty) {
      errors.aph_range_field_empty = { empty: true }
    }
    if (contractState.aph_field_empty) {
      errors.aph_field_empty = { empty: true }
    }
    if (contractState.planting_field_empty) {
      errors.planting_field_empty = { empty: true }
    }
    // if (
    //   contractState?.root?.contract_type !== 'fed-on-farm' &&
    //   contractState.delivery_schedule.length === 0
    // ) {
    //   errors.delivery_schedule = { empty: true }
    // }
    if (
      !contractState.reseller_details ||
      !contractState.reseller_details.company_id
    ) {
      errors.reseller_details = { empty: true }
    }
    if (contractState.storage.length === 0) {
      errors.storage = { empty: true }
    }
    if (
      contractState?.root?.contract_type === 'fed-on-farm' &&
      (!contractState?.cattle_info[0]?.unit_count ||
        !contractState?.cattle_info[0]?.cattle_type)
    ) {
      errors.delivery_schedule = { empty: false, cattle_empty: true }
    }
    if (contractState?.root?.contract_type !== 'fed-on-farm' && contractState.delivery_remaining_bushle_zero) {
      errors.delivery_remaining_bushle_zero = { empty: true }
    }
    if (contractState.field_aph_planting_rate) {
      errors.field_aph_planting_rate = { empty: true }
    }
    if (contractState && contractState.root.contract_type === 'generic' && contractState.fields.length === 1 && contractState.fields[0].field_id === 'total_enogen_acres_id') {
      const { planting_rate } = contractState.fields[0]
      const plantingRateNumber = Number(planting_rate)

      if (plantingRateNumber < 100000 || plantingRateNumber > 180000) {
        errors.planting_rate_invalid = { empty: true }
      }
    }

    if(contractState && contractState.delivery_schedule) {
      const monthlyUnit = contractState.delivery_schedule
      const notExistMonthInProdPlan = monthlyUnit.filter(unit => {
        let year = (unit.delivery_year).toString().length === 2 ? (unit.delivery_year).toString() : (unit.delivery_year).toString().slice(2)
        const monthYear= (unit.delivery_month).toString().slice(0,3) + '-' + year
        return (
          unit.contract_units !== '0' &&  
          unit.contract_units !== 0 && 
          existedMonthInProdPlan.indexOf(monthYear) === -1 && 
          unit
        )
      })

      if(notExistMonthInProdPlan.length > 0 ) {
        const notExistMonth = existedMonthInProdPlan.map((ele,i) => {
          return i === existedMonthInProdPlan.length-1 
            ? ele[0].toUpperCase() + ele.slice(1) 
            : ele[0].toUpperCase() + ele.slice(1) + ', '
        }).join('')

        errors.delivery_schedule = { 
          empty: false, 
          cattle_empty: false, 
          contract_unit_invalid: {
            state: true,
            value: `Bushels must be allocated in the following months per the product plan: ${notExistMonth}. Please contact support at 270-435-4369 or by emailing support@syngentadigital.com if you need further assistance.`
          } 
        }
      }
    }
    
    
    if (JSON.stringify(errors) === '{}' && !validDataError) {
      return true
    } else {
      contractDispatch({ type: 'SET_ERRORS', payload: errors })
      return false
    }
  }

  const submitContract = async () => {
    trackEvent('continue_create_contract_clicked')
    if (!checkForErrors()) {
      return
    }
   
    if (!contractState.errorStatus) {
      const newContractObj = {
        root: contractState.root,
        inventory_code: inventoryCode,
        grain_receiver_details: contractState.grain_receiver_details,
        reseller_details: contractState.reseller_details,
        grower_details: contractState.grower_details,
        fields: contractState.fields,
        aph_field_empty: contractState.aph_field_empty,
        planting_field_empty: contractState.planting_field_empty,
        delivery_schedule: contractState.delivery_schedule,
        storage: contractState.storage,
        cattle_info: contractState.cattle_info,
        delivery_remaining_bushle_zero: contractState.delivery_remaining_bushle_zero,
        field_aph_planting_rate: contractState.field_aph_planting_rate
      }

      try {
        contractDispatch({ type: 'ATTEMPT_POST_CONTRACT' })
        const newContractResponse = await createNewContractFieldSync(newContractObj, sfdcId)
        const newContractId = newContractResponse.contract_id
        const newContractDocLink = newContractResponse.contract_doc_link
        const new_contract_id = newContractResponse.new_contract_id
        const newTitle = `${contractState.root.contract_year} : ${contractState.grower_details.grower_name} to ${contractState.grain_receiver_details.company_name}`
        const dispatchObj = {
          company_id: contractState.root.external_reference_id,
          contractDetails: {
            contract_id: newContractId,
            contract_doc_link: newContractDocLink,
            reseller_details: contractState.reseller_details
          },
          detailView: {
            previous: '',
            title: newTitle,
            new_contract_id: new_contract_id
          }
        }
        dispatch({ type: 'GO_TO_CONTRACT_PDF_VIEW', payload: dispatchObj })
      } catch (err) {
        contractDispatch({
          type: 'SET_ERRORS',
          payload: { generic: { api_fail: true } }
        })
      }
    }
  }

  const prevGrowerSfdcId = useRef()
  useEffect(() => {

    const getDefaultStorage = () => {
      let defaultStorage = init_storage
        ? init_storage instanceof Map
          ? [...init_storage.values()]
          : Object.values(init_storage)
        : []

      if (defaultStorage.length > 0) {
        defaultStorage = defaultStorage.map(item => ({
          grower_id: item.grower_id || null,
          storage_locations_capacity: item.storage_capacity,
          storage_locations_id: item.storage_location_id,
          storage_locations_latitude: item.storage_latitude,
          storage_locations_longitude: item.storage_longitude,
          storage_locations_name: item.storage_name,
          storage_locations_type: item.storage_type
        }))
      }
      return defaultStorage
    }

    if (contractState?.grower_details?.grower_id) {
      if (
        contractState.grower_details.grower_id &&
        prevGrowerSfdcId.current !== contractState.grower_details.grower_id
      ) {
        getStorage('grower_id', contractState.grower_details.grower_id).then(
          res => {
            if (res?.data?.storage_locations && !canceled.current) {
              setStorageData(res.data.storage_locations)
            } else {
              const defaultStorage = getDefaultStorage()
              setStorageData(defaultStorage)
            }
          }
        )
        prevGrowerSfdcId.current = contractState.grower_details.grower_id
      }
    } else {
      const defaultStorage = getDefaultStorage()
      if (JSON.stringify(defaultStorage) !== JSON.stringify(storageData)) {
        setStorageData(defaultStorage)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps   
  }, [contractState])

  useEffect(() => {
    if (contractState.root.production_plan_id) {
      
      getProductionPlan(contractState.root.production_plan_id).then(
        response => {
          let aphVal = response.aph
          setInventoryCode(response.inventory_code)
          if (!canceled.current) {
            if (response?.monthly_units) {
              getContractCommittedUnits(contractState.root.production_plan_id).then(commited_result => {
               
                if (commited_result?.length) {
                  let checkMap = {}
                  let arrayOfAvailable = response.monthly_units.map(base_row => {
                    let resultForArrayOfAvailable = { ...base_row }
                    commited_result.forEach(commited_row => {
                                                                                                                                                                              
                      if (
                        base_row.month === commited_row.delivery_month 
                        && base_row.year === commited_row.delivery_year.toString() 
                        && base_row.end_product_type === commited_row.delivery_type) {
                        let available_units
                        let available_acres

                        const contract_unites_new = Math.round(commited_row.contract_units)
                        const contract_acres_new = Math.round(contract_unites_new / aphVal)
                        if (Object.prototype.hasOwnProperty.call(checkMap, commited_row.delivery_month) 
                          && checkMap[`${commited_row.delivery_month}`].end_product_type === commited_row.delivery_type) {    
                          available_units = checkMap[`${commited_row.delivery_month}`].available_units - contract_unites_new         
                          available_acres = checkMap[`${commited_row.delivery_month}`].available_acres - contract_acres_new
                        } else {
                          available_units = parseFloat(base_row.available_units) - contract_unites_new
                          available_acres = parseFloat(base_row.available_acres) - contract_acres_new
                        }
                        resultForArrayOfAvailable = { ...base_row, available_units, available_acres }
                        checkMap[`${base_row.month}`] = resultForArrayOfAvailable
                      }
                    })
                    return resultForArrayOfAvailable
                  })
                 
                  setDeliveryData(arrayOfAvailable)
                } else {
                  setDeliveryData(response.monthly_units)
                }
              })

              let existMonths = []
              response.monthly_units.forEach(base_row => {
                let year = (base_row.year).toString().length === 2 ? (base_row.year).toString() : (base_row.year).toString().slice(2)
                let monthYear= (base_row.month).toString().slice(0,3) + '-' + year
                if(existMonths.indexOf(monthYear) === -1){
                  existMonths.push(monthYear)
                }
                setExistMonthInProdPlan(existMonths)
              })
              setNewMonthlyUnits(true)
            }
          }
        }
      )
    }
  }, [contractState.root.production_plan_id])

  useEffect(() => {
    setHasPermissions(hasAccess('ANY', 'create-contract'))
  }, [hasAccess])

  useEffect(() => {
    const mapFields = fields => {
      const fieldMap = new Map()
      fields.forEach(field => {
        fieldMap.set(field.field_id, field)
      })
      return fieldMap
    }

    const mapStorageLocations = locations => {
      const storageMap = new Map()
      locations.forEach(location => {
        storageMap.set(location.storage_location_id, location)
      })
      return storageMap
    }
    const initAmmendContract = contractData => {
      const {
        grain_receiver_details,
        grower_details,
        reseller_details,
        display_details,
        initial_fields,
        storage,
        delivery_schedule,
        cattle_info
      } = contractData.contract_details
      const year = moment.utc(contractData.contract_start_execution).format('YYYY')
      const contract_language = contractData.contract_language
      const returnable = {
        grain_receiver_details,
        grower_details,
        reseller_details,
        display_details,
        contract_year: year,
        contract_language: contract_language
      }
      set_init_grain_receiver_details(grain_receiver_details)
      set_init_grower_details(grower_details)
      set_init_reseller_details(reseller_details)
      set_grain_receiver_id(grain_receiver_details.grain_receiver_id)
      set_init_contract_details({ type: display_details.contract_type, year, contract_language })
      set_reseller_id(reseller_details.sap_id)
      set_grower_id(grower_details.sap_id)
      set_init_fields(mapFields(initial_fields))
      set_init_storage(storage && mapStorageLocations(storage))
      if (display_details.contract_type === 'fed-on-farm') {
        set_init_cattle_info(cattle_info)
      } else {
        set_init_delivery_schedule(delivery_schedule)
      }
      contractDispatch({ type: 'INIT_AMMEND_CONTRACT', payload: returnable })
    }
    if (contract_id) {
      getContractById(contract_id).then(res => {
        if (res && !canceled.current) {
          initAmmendContract(res)
        }
      })
    }
  }, [contract_id])

  useEffect(() => {
    const resellerSAPId = contractState.reseller_details?.sap_id
    const contractYear = contractState.root?.contract_year
    if(resellerSAPId && contractYear && !contract_id){
      getTraining(resellerSAPId, contractYear).then(res => {
        if(res.data.success === 'true'){
          if(res.data.active === 'true' || res.data.active === true){
            setValidDataError(false)
          } else {
            setValidDataError(true)
          }
        } else {
          setValidDataError(true)
        }
      })
    }
  }, [contractState.reseller_details, contractState.root, contract_id])

  const generateErrors = () => {
    const errorArray = []
    for (let field in contractState.errors) {
      for (let errorType in contractState.errors[field]) {
        if (contractState.errors[field][errorType].state) {
          const errorText = contractState.errors[field][errorType].value
          errorArray.push(
            <p className="c-red mb0 pb0" key={errorText}>
              {errorText}
            </p>
          )
        }
      }
    }
    if (errorArray.length) {
      return errorArray
    }
    return null
  }

  const generateRightHandSide = () => {
    if (contractState.rightHandStatus === 'ERROR') {
      return (
        <div className="posf">
          {validDataError ? 
            <p className="c-red mb0 pb0">
            Unable to Create Contract with selected Reseller, Stewardship Training must be completed for selected Reseller
            </p>
            : null}
          {generateErrors()}
        </div>
      )
    } else if (contractState.rightHandStatus === 'CREATING') {
      return (
        <div className="posf">
          <p className="c-green">Please wait... Generating contract</p>
          <TableStatus status="LOADING" />
        </div>
      )
    } else {
      return null
    }
  }

  let detailViewButton = null
  let leftComponents = null
  if (hasPermissison === 'SUCCESS') {
    detailViewButton = (
      <div data-testid="createContractContinue">
        <Button
          styleType="success"
          clickHandler={submitContract}
          customClassName="mb10"
          disabled= {fieldDataStatus === 'LOADING' || fieldDataStatus === 'WAITING'}
        >
          Continue
        </Button>
      </div>
    )
    leftComponents = (
      <>
        <NewContractDetailsWidget
          _resellerId={reseller_id}
          _grainReceiverId={grain_receiver_id}
          _threePOId={threePO_id}
          _growerId={grower_id}
          updateRoot={contractDispatch}
          fieldDataCallback={fieldDataCallback}
          initGrainReceiverDetails={init_grain_receiver_details}
          initGrowerDetails={init_grower_details}
          initResellerDetails={init_reseller_details}
          _contractType={init_contract_details?.type}
          _contractYear={init_contract_details?.year}
          _contractProductionPlanId={contractState?.root?.production_plan_id}
          _contractLanguage={init_contract_details?.contract_language}
          _grain_receiver_details={contractState.grain_receiver_details}
        />
        <FieldWithTable
          status={
            contractState.root.contract_year ? fieldDataStatus : 'WAITING'
          }
          farmArray={fieldData.farmArray || emptyArray}
          fieldArray={fieldData.fields || emptyArray}
          getFieldRowData={setFieldFormData}
          updateFields={contractDispatch}
          contractYear={contractState.root.contract_year}
          _defaultFields={init_fields}
          setTotalAPH={setTotalAPH}
          setDefaultAphEvent={setDefaultAphEvent}
          setResetDeliveryTable={setResetDeliveryTable}
          contractType = {contractState.root.contract_type}
        />
        <Storage
          postStorageLocation={postStorageLocation}
          grower_id={contractState?.grower_details?.grower_id}
          contractDispatch={contractDispatch}
          storageData={storageData}
          updateStorage={contractDispatch}
          _defaultStorage={init_storage} 
          operationType={contract_id ? 'amend' : 'create'}         
        />
        { contractState &&
            <DeliveryScheduleWidget
              contractType={contractState?.root?.contract_type}
              contractYear={contractState.root?.contract_year}
              initialMonthlyUnits={deliveryData}
              fieldData={contractState.fields}
              returnData={contractDispatch}
              newMonthlyUnits={newMonthlyUnits}
              updatedMonthlyUnits={setNewMonthlyUnits}
              _defaultDeliverySchedule={init_delivery_schedule}
              _defaultCattleInfo={init_cattle_info}
              totalAPH = {totalAPH}
              defaultAphEvent = {defaultAphEvent}
              satusResetDeliveryTable = {satusResetDeliveryTable}
              setSatusResetDeliveryTable = {setSatusResetDeliveryTable}
            />
        }
        <EstimatedUnits fields={contractState.fields} root = {contractState.root} loadingStatus = {fieldDataStatus}/>
        <div className="tar mt20 mb20">{detailViewButton}</div>
      </>
    )
  } else if (!hasPermissison) {
    leftComponents = (
      <>
        <AccessDeniedView />
      </>
    )
  } else {
    leftComponents = (
      <>
        <TableStatus status="LOADING" />
      </>
    )
  }
  
  return (
    <DetailView button={detailViewButton}>
      <>{leftComponents}</>
      <div>
        {generateRightHandSide()}
      </div>
    </DetailView>
  )
}

export default CreateContractView
